import { readPublicEnvironment } from './environment';

const apiBaseUrl = (readPublicEnvironment('VITE_API_BASE_URL') || 'http://localhost:8000/api/v1').replace(/\/$/, '');

export interface AccountDeletionRequestPayload {
  submission_id: string;
  email: string;
  privacy_acknowledged: boolean;
  privacy_version: string;
  turnstile_token: string;
  website: string;
}

export class AccountDeletionApiError extends Error {
  constructor(message: string, readonly status: number, readonly details: unknown) {
    super(message);
    this.name = 'AccountDeletionApiError';
  }
}

async function request<T>(path: string, payload: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (cause) {
    throw new AccountDeletionApiError('The account deletion service could not be reached.', 0, cause);
  }
  const body: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = typeof body === 'object' && body && 'detail' in body && typeof body.detail === 'string'
      ? body.detail
      : `Request failed with status ${response.status}`;
    throw new AccountDeletionApiError(message, response.status, body);
  }
  return body as T;
}

export function submitAccountDeletionRequest(payload: AccountDeletionRequestPayload): Promise<{ detail: string }> {
  return request('/public/account-deletion-requests/', payload);
}

export function confirmAccountDeletionRequest(verificationToken: string): Promise<{ detail: string }> {
  return request('/public/account-deletion-requests/confirm/', { verification_token: verificationToken });
}

export function accountDeletionErrorMessage(error: unknown): string {
  if (!(error instanceof AccountDeletionApiError)) return 'The request could not be submitted. Please try again.';
  if (error.status === 429) return 'Too many requests were submitted. Please wait and try again.';
  if (error.status === 503 || error.status === 0) return 'The account deletion service is temporarily unavailable. Please try again later.';
  if (error.details && typeof error.details === 'object') {
    const details = error.details as Record<string, unknown>;
    const messages: string[] = [];
    const collect = (value: unknown) => {
      if (typeof value === 'string') messages.push(value);
      else if (Array.isArray(value)) value.forEach(collect);
      else if (value && typeof value === 'object') Object.values(value).forEach(collect);
    };
    collect(details.errors ?? details);
    if (messages[0]) return messages[0];
  }
  return error.message;
}
